import {
  isWeightedUnitSystem,
  WeightUnitDefinitions,
  WeightedUnitSystemRegistry,
  type ProductUnitSystem,
  type WeightUnit,
  type WeightedUnitSystemDefinition
} from "@kanpla/api-contract";

import { t } from "@repo/transifex";

export const getPricingBasis = (
  unitSystem: ProductUnitSystem | undefined
): WeightedUnitSystemDefinition | null =>
  isWeightedUnitSystem(unitSystem)
    ? WeightedUnitSystemRegistry[unitSystem]
    : null;

/**
 * Converts within one unit system. A cross-system pair is a caller bug: gate on
 * `isSameUnitSystem` first, a reading in the wrong system is never converted.
 */
export const convertWeight = (
  value: number,
  from: WeightUnit,
  to: WeightUnit
): number =>
  (value * WeightUnitDefinitions[from].perBaseUnit) /
  WeightUnitDefinitions[to].perBaseUnit;

export const isSameUnitSystem = (a: WeightUnit, b: WeightUnit): boolean =>
  WeightUnitDefinitions[a].system === WeightUnitDefinitions[b].system;

/**
 * The literals have to sit inside `t()`: the Transifex extractor is static and
 * the push runs with `--purge`, so a source string it cannot see is deleted
 * along with its translations.
 */
export const getScaleUnitLabel = (unit: WeightUnit): string => {
  switch (unit) {
    case "lb":
      return t("lb");
    case "oz":
      return t("oz");
    default:
      return t("grams");
  }
};

/**
 * A wrapper that predates the unit fields reports no decimals while a PD-II
 * still weighs in tenths of a gram: `toFixed(0)` would round that tenth away,
 * so a fractional reading without decimals is printed as it arrived.
 */
export const formatScaleWeight = (weight: number, decimals: number): string =>
  decimals === 0 && !Number.isInteger(weight)
    ? String(weight)
    : weight.toFixed(decimals);

/** A reading with its unit, as the customer sees it: "1.350 lb", "248 grams". */
export const formatWeightReading = ({
  weight,
  unit,
  decimals
}: {
  weight: number;
  unit: WeightUnit;
  decimals: number;
}): string =>
  `${formatScaleWeight(weight, decimals)} ${getScaleUnitLabel(unit)}`;
